import { AlertCircle, ArrowUpRight, ExternalLink, Globe, X } from 'lucide-react'
import type { PatentBrowseResult } from '../../services/api'
import { googlePatentsUrl } from '../../utils/patentUtils'
import LoadingSkeleton from '../shared/LoadingSkeleton'

interface PatentPreviewPanelProps {
  patentId: string
  patent?: PatentBrowseResult
  isLoading: boolean
  isError: boolean
  onClose: () => void
  onOpenFull: () => void
}

/**
 * Inline "detail preview" rail shown alongside the results list (Fig43-style)
 * without navigating away — the full record (/patents/{id}) stays a separate,
 * already-implemented page reachable via "Open full record".
 *
 * Only ever renders fields the API actually returns. Real patents from the
 * patent-embeddings index do not carry a claims/cited-by field (only the
 * suppressed local-corpus fallback entries do, and those never reach this
 * panel) — so "Key claims" is shown only when the data is present, and
 * "Cited by" is omitted entirely rather than faked.
 */
export default function PatentPreviewPanel({ patentId, patent, isLoading, isError, onClose, onOpenFull }: PatentPreviewPanelProps) {
  const claims = (patent as unknown as { claims?: string[] } | undefined)?.claims

  return (
    <div className="hidden xl:block w-80 flex-shrink-0">
      <div className="bg-white rounded-lg border border-gray-200 p-4 sticky top-4 max-h-[80vh] overflow-y-auto" data-testid="patent-preview-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700">Preview</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600" aria-label="Close preview">
            <X size={16} />
          </button>
        </div>

        {isLoading && <LoadingSkeleton rows={3} type="card" />}

        {!isLoading && (isError || !patent) && (
          <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span>Patent {patentId} not found in local index.</span>
          </div>
        )}

        {!isLoading && !isError && patent && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-[10px] font-mono text-iflow-600 bg-iflow-50 px-1.5 py-0.5 rounded">{patent.patent_id}</span>
                {patent.jurisdiction && (
                  <span className="text-[10px] text-gray-500 flex items-center gap-0.5">
                    <Globe size={10} /> {patent.jurisdiction}
                  </span>
                )}
              </div>
              <h4 className="text-sm font-semibold text-gray-900">{patent.title}</h4>
              {patent.assignee && <p className="text-xs text-gray-500 mt-0.5">{patent.assignee}</p>}
            </div>

            {patent.abstract && (
              <div>
                <h5 className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Abstract</h5>
                <p className="text-xs text-gray-700 leading-relaxed">{patent.abstract}</p>
              </div>
            )}

            {claims && claims.length > 0 && (
              <div>
                <h5 className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">Key claims</h5>
                <ul className="space-y-1.5">
                  {claims.slice(0, 3).map((c, i) => (
                    <li key={i} className="text-xs text-gray-700 leading-relaxed">{c}</li>
                  ))}
                </ul>
              </div>
            )}

            {patent.cpc_codes && patent.cpc_codes.length > 0 && (
              <div>
                <h5 className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 mb-1">CPC codes</h5>
                <div className="flex flex-wrap gap-1">
                  {patent.cpc_codes.map((c, i) => (
                    <span key={i} className="text-[10px] font-mono text-gray-700 bg-gray-100 px-1.5 py-0.5 rounded">{c}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="text-[10px] text-gray-500 flex flex-wrap gap-3">
              {patent.filing_date && <span>Filed {patent.filing_date}</span>}
              {patent.publication_date && <span>Published {patent.publication_date}</span>}
            </div>

            <button
              type="button"
              onClick={onOpenFull}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-iflow-700 bg-iflow-50 rounded-lg hover:bg-iflow-100"
            >
              Open full record <ArrowUpRight size={12} />
            </button>

            {googlePatentsUrl(patent) && (
              <a
                href={googlePatentsUrl(patent) as string}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                View full patent on Google Patents <ExternalLink size={12} />
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
